// Hook: useCanvasViewPrefs.js
// Purpose: Persist validated Strategy Builder Canvas view preferences in guarded browser storage.
import { useCallback, useEffect, useRef, useState } from 'react';

export const CANVAS_VIEW_PREFS_KEY = 'deetrade.builder.canvasView.v1';
export const DEFAULT_OUTLINE_WIDTH = 360;
export const MIN_OUTLINE_WIDTH = 240;

const DEFAULT_PREFS = Object.freeze({
  subView: 'flow', outlineOpen: false, labelMode: 'names', outlineWidth: DEFAULT_OUTLINE_WIDTH,
});
const VALID_SUB_VIEWS = new Set(['flow', 'tree']);
const VALID_LABEL_MODES = new Set(['names', 'fields']);

const readPrefs = () => {
  try {
    const raw = window.localStorage.getItem(CANVAS_VIEW_PREFS_KEY);
    if (raw == null) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw);
    return {
      subView: VALID_SUB_VIEWS.has(parsed?.subView) ? parsed.subView : DEFAULT_PREFS.subView,
      outlineOpen: typeof parsed?.outlineOpen === 'boolean' ? parsed.outlineOpen : DEFAULT_PREFS.outlineOpen,
      labelMode: VALID_LABEL_MODES.has(parsed?.labelMode) ? parsed.labelMode : DEFAULT_PREFS.labelMode,
      outlineWidth: Number.isFinite(parsed?.outlineWidth) && parsed.outlineWidth >= MIN_OUTLINE_WIDTH
        ? parsed.outlineWidth
        : DEFAULT_PREFS.outlineWidth,
    };
  } catch (_) {
    return DEFAULT_PREFS;
  }
};

const useCanvasViewPrefs = () => {
  const [prefs, setPrefs] = useState(readPrefs);
  const firstPersist = useRef(true);

  useEffect(() => {
    if (firstPersist.current) {
      firstPersist.current = false;
      return;
    }
    try {
      window.localStorage.setItem(CANVAS_VIEW_PREFS_KEY, JSON.stringify(prefs));
    } catch (_) {
      // Browser privacy modes may deny storage; in-memory preferences remain usable.
    }
  }, [prefs]);

  const setSubView = useCallback((subView) => {
    if (!VALID_SUB_VIEWS.has(subView)) return;
    setPrefs((current) => ({ ...current, subView }));
  }, []);

  const setOutlineOpen = useCallback((outlineOpen) => {
    setPrefs((current) => ({ ...current, outlineOpen: Boolean(outlineOpen) }));
  }, []);

  const setLabelMode = useCallback((labelMode) => {
    if (!VALID_LABEL_MODES.has(labelMode)) return;
    setPrefs((current) => ({ ...current, labelMode }));
  }, []);

  const setOutlineWidth = useCallback((outlineWidth) => {
    if (!Number.isFinite(outlineWidth) || outlineWidth < MIN_OUTLINE_WIDTH) return;
    setPrefs((current) => ({ ...current, outlineWidth }));
  }, []);

  return {
    subView: prefs.subView,
    outlineOpen: prefs.outlineOpen,
    labelMode: prefs.labelMode,
    outlineWidth: prefs.outlineWidth,
    setSubView,
    setOutlineOpen,
    setLabelMode,
    setOutlineWidth,
  };
};

export default useCanvasViewPrefs;
